<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useEventsListStore } from '@/js/core/stores/events-list-store';
import { useCurrentPageStore } from '@/js/core/stores/current-page-store';
import { useLoadingStore } from '@/js/core/stores/loading-store';
import PageHeading from '@/js/core/components/parts/PageHeading.vue';
import UserBar from '@/js/core/components/parts/user-bar/UserBar.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import CalendarFilters from '@/js/core/components/parts/calendar/CalendarFilters.vue';
import EventViewCalendar from '@/js/core/components/parts/calendar/EventViewCalendar.vue';
import EventViewCategories from '@/js/core/components/parts/calendar/EventViewCategories.vue';
import EventViewList from '@/js/core/components/parts/calendar/EventViewList.vue';
import { useTimezoneStore } from '@/js/core/stores/timezone-store';
import { useProfileStore } from '@/js/core/stores/profile-store';
import { useUserStore } from '@/js/core/stores/user-store';
import {
  currentMonthNumber,
  monthToCalendarGridRange,
  monthToRange,
} from '@/js/core/utils/month-range.js';
import { useCalendarEvents } from '@/js/core/composables/useCalendarEvents.js';
import { useCalendarFilterOptions } from '@/js/core/composables/useCalendarFilterOptions.js';
import { usePastEventsToggle } from '@/js/core/composables/usePastEventsToggle';
import { useCalendarUrlState } from '@/js/core/composables/useCalendarUrlState';
import { parseCalendarState } from '@/js/core/utils/calendar-url-state';
import {
  createDefaultCalendarFilters,
  normalizeCalendarFilters,
} from '@/js/core/utils/calendar-filter-state';
import {
  calendarVisibleDateRange,
  eventFilterGroupsFromOptions,
} from '@/js/core/utils/calendar-events';
import { hasPastCalendarEvents } from '@/js/core/utils/calendar-event-projections';
import { canAccessFullAcademicYear } from '@/js/core/utils/full-academic-year-access';
import { audienceGroupLabel } from '@/js/core/utils/audience-group';
import CalendarInfo from '@/js/core/components/parts/calendar/CalendarInfo.vue';

const route = useRoute();
const eventsStore = useEventsListStore();
const currentPageStore = useCurrentPageStore();
const timezoneStore = useTimezoneStore();
const profileStore = useProfileStore();
const userStore = useUserStore();
const loadingStore = useLoadingStore();

const errorMessage = computed(() => eventsStore.error); // Add this to catch errors
const audienceGroup = computed(() => audienceGroupLabel(profileStore.profile, userStore.isGuest));
const canViewFullAcademicYear = computed(() => canAccessFullAcademicYear());

const timezoneLabelFormatted = computed(() => {
  const label = timezoneStore.effectiveTimezoneLabel || '';

  // Checks if the label already ends with or includes "time" (case-insensitive)
  if (label.toLowerCase().includes('time')) {
    return `${label}.`;
  }

  return `${label} time.`;
});

// Seed page state from URL query if present, so deep links work.
const initialUrlState = parseCalendarState(window.location.search);

const selectedFilters = ref(
  normalizeCalendarFilters(initialUrlState.filters, createDefaultCalendarFilters())
);

const initialYear = initialUrlState.dstart
  ? parseInt(initialUrlState.dstart.split('-')[0], 10)
  : new Date().getFullYear();
const initialMonthStr = initialUrlState.dstart
  ? initialUrlState.dstart.split('-')[1]
  : currentMonthNumber();

const currentDateRange = ref([monthToRange(initialYear, initialMonthStr)]);

const handleDateChange = range => {
  currentDateRange.value = range;
};

// What the event views actually render. `currentDateRange` moves the moment the month picker
// is clicked, but the views filter their events by the range they are given, so pointing them
// straight at it makes the outgoing month re-filter against the incoming one and briefly show
// only the few days of overspill the calendar-grid fetch range happened to include. This
// snapshot advances only once the events and filter options for the new range have both
// landed, so a month change repaints once.
// The range is a ref of its own rather than part of the snapshot below: usePastEventsToggle
// watches it deeply, and a deep watcher re-runs on every trigger even when the value is
// unchanged, so folding it into an object that is replaced on each commit would reset the
// past-events toggle the moment the user turned it on.
const displayedRange = ref(currentDateRange.value);
const displayed = ref({ events: [], filterGroups: [] });
const hasCommitted = ref(false);
// Commits store the same array reference they were given, so identity is enough here.
const isViewingDisplayedRange = computed(() => displayedRange.value === currentDateRange.value);

// For calendar view, need to access the previous and next month events for any overspill on weeks intersecting month breaks
const fetchDateRange = computed(() =>
  currentDateRange.value.map(range => {
    if (!range?.start_after) return range;
    const [year, monthStr] = range.start_after.split('-');
    return monthToCalendarGridRange(parseInt(year, 10), monthStr);
  })
);

// This page is also the backdrop for the event modal, where it is completely covered. Loading
// only once the calendar itself is the visible route keeps a month of events and two filter
// aggregates off the modal's critical path, and skips them entirely when the modal is closed
// back to the page it was opened from rather than to the calendar.
const canLoadCalendarData = computed(() => route.name === 'calendar');

const { filteredEvents, isCurrentRangeLoaded: eventsReady } = useCalendarEvents(
  eventsStore,
  fetchDateRange,
  selectedFilters,
  false,
  {},
  canLoadCalendarData
);

// Define the available views
const views = {
  list: EventViewList,
  calendar: EventViewCalendar,
  categories: EventViewCategories,
};

const activeView = ref(
  initialUrlState.view || profileStore.profile?.preferences?.calendar_last_view_mode || 'list'
);

const hideExpiredEvents =
  window.gsdNowAppData?.appConfiguration?.calendarHideExpiredEvents === true;

const canTogglePastEvents = computed(() =>
  hasPastCalendarEvents(displayed.value.events, {
    activeRange: displayedRange.value[0],
    timezone: timezoneStore.effectiveTimezone,
    hideExpiredEvents,
  })
);

// Past-events state lives at the page level so the URL writer can read it. It tracks the month
// on screen rather than the requested one, so the outgoing month keeps its expanded past events
// until it is replaced; the reset then lands with the rest of the commit.
const { showPastEvents, togglePastEvents } = usePastEventsToggle(displayedRange, {
  initialValue: initialUrlState.includes_past_events,
});

const filterOptionDateRange = computed(() => {
  const range = currentDateRange.value[0];

  if (!range) {
    return currentDateRange.value;
  }

  // Because showPastEvents now lags a month behind, ignore it while a new month is in flight,
  // or the outgoing month's toggle would widen the incoming month's request.
  const includesPastEvents = isViewingDisplayedRange.value && showPastEvents.value;

  return [calendarVisibleDateRange(range, timezoneStore.effectiveTimezone, includesPastEvents)];
});

const {
  filterGroups,
  academicYears,
  isCurrentRangeLoaded: filterOptionsReady,
} = useCalendarFilterOptions(filterOptionDateRange, {
  enabled: canLoadCalendarData,
});

// Both requests run against different ranges and resolve independently; holding the commit for
// both keeps grouping and card colours from shifting in a second pass after the events land.
const isDisplayReady = computed(() => eventsReady.value && filterOptionsReady.value);

const eventFilterGroups = computed(() =>
  eventFilterGroupsFromOptions(displayed.value.filterGroups)
);
const dstartForUrl = computed(() => filterOptionDateRange.value[0]?.start_after || null);

useCalendarUrlState({
  dstart: dstartForUrl,
  activeView,
  selectedFilters,
  showPastEvents,
});

// Registered after the composables above so it can read their readiness flags. Snapshotting the
// events matters as much as the range: when the events resolve before the filter options,
// filteredEvents has already flipped to the new month while the old one is still on screen.
watch(
  [isDisplayReady, filteredEvents, filterGroups],
  () => {
    if (!isDisplayReady.value) {
      return;
    }

    // Assigning the same array reference is a no-op on a ref, so this only wakes the
    // past-events reset when the month genuinely moved.
    displayedRange.value = currentDateRange.value;
    displayed.value = {
      events: filteredEvents.value,
      filterGroups: filterGroups.value,
    };
    hasCommitted.value = true;
  },
  { immediate: true }
);

// Surface the wait in the existing page progress bar, since the content below stays put. A
// failed request settles it too, otherwise the bar would run for as long as the page is open.
watch([isDisplayReady, errorMessage], () => {
  if (!canLoadCalendarData.value) {
    return;
  }

  if (isDisplayReady.value || errorMessage.value) {
    loadingStore.complete();
  } else {
    loadingStore.start();
  }
});

// Adds day/night attribute to content container based on current time
function updateDayNight(currentTime) {
  const gradient = document.querySelector('.gradient-container');
  const nav = document.querySelector('#nav-bar');
  const eventModal = document.querySelector('.full-page-modal__container');

  const hour = currentTime.getHours();
  const isDay = hour >= 6 && hour < 18;

  // GRADIENT CONTAINER
  if (gradient) {
    if (isDay) {
      gradient.setAttribute('data-day', '');
      gradient.removeAttribute('data-night');
    } else {
      gradient.setAttribute('data-night', '');
      gradient.removeAttribute('data-day');
    }
  }

  // NAV BAR
  if (nav) {
    if (isDay) {
      nav.setAttribute('data-day', '');
      nav.removeAttribute('data-night');
    } else {
      nav.setAttribute('data-night', '');
      nav.removeAttribute('data-day');
    }
  }

  if (eventModal) {
    if (isDay) {
      eventModal.setAttribute('data-day', '');
      eventModal.removeAttribute('data-night');
    } else {
      eventModal.setAttribute('data-night', '');
      eventModal.removeAttribute('data-day');
    }
  }
}

onMounted(async () => {
  await profileStore.fetchCurrentProfile();
  const currentTime = timezoneStore.currentTime;
  updateDayNight(currentTime);
});

// Leaving mid-request would otherwise strand the progress bar.
onUnmounted(() => {
  loadingStore.complete();
});
</script>

<template>
  <UserBar />
  <NavigationPanel />
  <main id="main-content" class="calendar-page-main-content">
    <section id="page-content" class="calendar-page">
      <header class="calendar-page-header">
        <PageHeading
          :title="currentPageStore.title || 'Calendar'"
          :description="currentPageStore.infoContent"
        />
      </header>
      <CalendarFilters
        v-model:view="activeView"
        v-model:filters="selectedFilters"
        :selected-months="currentDateRange"
        :academic-years="academicYears"
        :filter-groups="filterGroups"
        :academic-year-link-visible="canViewFullAcademicYear"
        @months-selection-changed="handleDateChange"
      />
      <div class="calendar-page-content">
        <div v-if="!hasCommitted && errorMessage">{{ errorMessage }}</div>
        <div v-else-if="!hasCommitted" class="calendar-page-loading-message">Loading events...</div>

        <template v-else>
          <div v-if="errorMessage">{{ errorMessage }}</div>

          <component
            :is="views[activeView]"
            :events="displayed.events"
            :active-range="displayedRange[0]"
            :timezone="timezoneStore.effectiveTimezone"
            :can-toggle-past-events="canTogglePastEvents"
            :show-past-events="showPastEvents"
            :hide-expired-events="hideExpiredEvents"
            :event-filter-groups="eventFilterGroups"
            @toggle-past-events="togglePastEvents"
          />
        </template>
      </div>
      <CalendarInfo
        :audience-group="audienceGroup"
        :timezone-label-formatted="timezoneLabelFormatted"
        :is-guest="userStore.isGuest"
      />
    </section>
  </main>
</template>
